
import {
  Users,
  FileText,
  ShieldCheck,
  CreditCard,
  Settings,
  HelpCircle,
  LogOut,
  Search,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useAuth } from "@/hooks/useAuth";
import { useLocation } from "react-router-dom";
import { Tooltip } from "@/components/ui/tooltip-custom";
import { CreditBalanceCompact } from "@/components/credits";

interface CollapsedSidebarProps {
  onLogout: () => void;
  onNavigate: (route: string) => void;
}

export const CollapsedSidebar = ({
  onLogout,
  onNavigate
}: CollapsedSidebarProps) => {
  const location = useLocation();
  const { profile } = useAuth();

  const mainMenuItems = [
    { icon: Users, label: 'Mon équipe', path: '/mon-equipe' },
    { icon: Search, label: 'Prise de référence', path: '/dashboard' },
    { icon: FileText, label: 'Rapport', path: '/reports' },
    { icon: ShieldCheck, label: 'Conformité', path: '/conformite' },
    { icon: CreditCard, label: 'Abonnement', path: '/subscription' },
  ];

  const settingsMenuItems = [
    { icon: Settings, label: 'Paramètres', path: '/settings' },
    { icon: HelpCircle, label: 'Support', path: '/help-support' },
  ];

  // Gestionnaire pour l'achat de crédits
  const handleBuyCredits = () => {
    onNavigate('/subscription');
  };

  return (
    <div
      className="fixed left-0 top-0 z-50 flex h-screen w-[56px] flex-shrink-0 flex-col border-r border-[hsl(var(--app-sidebar-border))]"
      style={{ backgroundColor: 'hsl(var(--app-sidebar-bg))' }}
    >

      {/* Header with Logo */}
      <div className="flex w-full flex-col items-center px-2 pb-3 pt-4">
        {/* Logo */}
        <Tooltip content="Dashboard" position="right">
          <img
            src="https://pvmemyboxgefsaordzqj.supabase.co/storage/v1/object/public/App%20image/Icone%20Getproof.svg"
            alt="Getproof Icon"
            className="w-8 h-8 object-contain cursor-pointer hover:opacity-80 transition-opacity"
            onClick={() => onNavigate('/mon-equipe')}
          />
        </Tooltip>
      </div>

      {/* Main Content */}
      <div className="flex-1 px-2 w-full mt-2">
        <div className="flex w-full flex-col items-center">
          {/* Main Navigation */}
          {mainMenuItems.map((item, index) => (
            <Tooltip key={item.path} content={item.label} position="right">
              <div
                className={`flex justify-center items-center p-2 w-10 h-10 rounded-lg cursor-pointer transition-all duration-200 ${index === 0 ? '' : 'mt-1.5'
                  } ${location.pathname === item.path
                    ? 'text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]'
                    : 'text-[hsl(var(--app-sidebar-text-muted))] hover:bg-white/[0.035] hover:text-white'
                  }`}
                style={location.pathname === item.path ? { backgroundColor: 'hsl(var(--app-sidebar-surface))', border: '1px solid rgba(255,255,255,0.06)' } : {}}
                onClick={() => onNavigate(item.path)}
                role="button"
                tabIndex={0}
                aria-label={item.label}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onNavigate(item.path);
                  }
                }}
              >
                <item.icon className="w-5 h-5" />
              </div>
            </Tooltip>
          ))}
        </div>

        {/* Separator */}
        <div className="mx-auto mt-6 h-[1px] w-10 bg-white/8"></div>

        {/* Settings Navigation */}
        <div className="flex flex-col items-center mt-6 w-full">
          {settingsMenuItems.map((item, index) => (
            <Tooltip key={item.path} content={item.label} position="right">
              <div
                className={`flex justify-center items-center p-2 w-10 h-10 rounded-lg cursor-pointer transition-all duration-200 ${index === 0 ? '' : 'mt-1.5'
                  } ${location.pathname === item.path
                    ? 'text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]'
                    : 'text-[hsl(var(--app-sidebar-text-muted))] hover:bg-white/[0.035] hover:text-white'
                  }`}
                style={location.pathname === item.path ? { backgroundColor: 'hsl(var(--app-sidebar-surface))', border: '1px solid rgba(255,255,255,0.06)' } : {}}
                onClick={() => onNavigate(item.path)}
                role="button"
                tabIndex={0}
                aria-label={item.label}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onNavigate(item.path);
                  }
                }}
              >
                <item.icon className="w-5 h-5" />
              </div>
            </Tooltip>
          ))}
        </div>

        {/* Credits Section avec mises à jour en temps réel */}
        <CreditBalanceCompact 
          onBuyCredits={handleBuyCredits}
          className="mt-6"
        />
      </div>

      {/* Footer */}
      <div className="flex flex-col justify-center px-2 w-full pb-4">
        {/* Additional separator */}
        <div className="mx-auto mb-4 h-[1px] w-10 bg-white/8"></div>

        {/* User Avatar and Logout */}
        <div className="flex flex-col items-center space-y-2 mb-4">
          <Tooltip content={`${profile?.first_name || 'Utilisateur'} - ${profile?.email || ''}`} position="right">
            <Avatar className="w-8 h-8 rounded-full cursor-pointer hover:opacity-80 transition-opacity">
              <AvatarImage src={profile?.avatar_url || undefined} alt="Photo de profil" />
              <AvatarFallback className="text-white font-medium text-sm" style={{ backgroundColor: '#40E0D0' }}>
                {profile?.first_name?.[0]?.toUpperCase() || 'U'}
              </AvatarFallback>
            </Avatar>
          </Tooltip>
          
          <Tooltip content="Se déconnecter" position="right">
            <Button
              variant="ghost"
              size="sm"
              onClick={onLogout}
              className="h-8 w-8 rounded-lg p-0 transition-colors hover:bg-white/5"
              aria-label="Se déconnecter"
            >
              <LogOut className="h-4 w-4 text-red-400 hover:text-red-300" />
            </Button>
          </Tooltip>
        </div>
      </div>
    </div>
  );
};
